/*
 * Builds Hyperpro's private order part number from the active Shopify variant
 * and the rider configuration captured by the product form.
 */
(function (global) {
  'use strict';

  var WEIGHT_CODES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ12345678';
  var HEIGHT_CODES = 'CDEFGHIJKLMNOPQRSTUVW';
  var BASE_SKU_LENGTH = 8;

  function normalize(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function numberFrom(value) {
    var match = String(value == null ? '' : value).match(/[-+]?\d*\.?\d+/);
    return match ? Number(match[0]) : NaN;
  }

  function weightCodeFromPounds(pounds) {
    var numericPounds = numberFrom(pounds);
    if (!Number.isFinite(numericPounds)) return '';

    var roundedKg = Math.round(numericPounds / 2.2046226218 / 5) * 5;
    var codeIndex = roundedKg / 5 - 1;
    return Number.isInteger(codeIndex) && codeIndex >= 0 && codeIndex < WEIGHT_CODES.length
      ? WEIGHT_CODES.charAt(codeIndex)
      : '';
  }

  function heightCodeFromMillimeters(height) {
    var numericHeight = numberFrom(height);
    if (!Number.isFinite(numericHeight)) return '';

    var roundedHeight = Math.round(numericHeight / 5) * 5;
    var codeIndex = (roundedHeight + 60) / 5;
    return Number.isInteger(codeIndex) && codeIndex >= 0 && codeIndex < HEIGHT_CODES.length
      ? HEIGHT_CODES.charAt(codeIndex)
      : '';
  }

  function loweringMillimetersFromRemarks(remarks) {
    var text = String(remarks == null ? '' : remarks);
    var loweringMatches = Array.from(text.matchAll(/\blow(?:er(?:ed|ing)?)?\b/gi));
    if (!loweringMatches.length) return null;

    var measurementMatches = Array.from(
      text.matchAll(/([-+]?(?:\d+(?:\.\d+)?|\.\d+))\s*(mm\b|inches?\b|in(?:\.|\b)|")/gi)
    );
    var numberMatches = measurementMatches.length
      ? measurementMatches
      : Array.from(text.matchAll(/[-+]?(?:\d+(?:\.\d+)?|\.\d+)/g));
    if (!numberMatches.length) return null;

    var closest = null;
    var closestDistance = Infinity;
    loweringMatches.forEach(function (loweringMatch) {
      numberMatches.forEach(function (numberMatch) {
        var distance = Math.abs(numberMatch.index - loweringMatch.index);
        if (distance < closestDistance) {
          closest = numberMatch;
          closestDistance = distance;
        }
      });
    });

    var millimeters = closest ? numberFrom(closest[1] || closest[0]) : NaN;
    var unit = closest && closest[2] ? normalize(closest[2]) : 'mm';
    if (unit === '"' || unit.indexOf('in') === 0) millimeters *= 25.4;
    return Number.isFinite(millimeters) ? -Math.abs(millimeters) : null;
  }

  function dampingCode(ridingStyle, stiffness) {
    var style = normalize(ridingStyle);
    var setting = normalize(stiffness);

    if (style.indexOf('off-road') !== -1 || style.indexOf('off road') !== -1 || style.indexOf('enduro') !== -1) {
      return 'E';
    }
    if (setting.indexOf('comfort') !== -1) return 'C';
    if (setting.indexOf('standard') !== -1) return 'S';
    if (setting.indexOf('sport') !== -1) return 'R';
    return '';
  }

  function lengthAdjusterCode(value) {
    var option = normalize(value);
    if (!option || option === 'none' || option === 'no') return 'X';
    return option.indexOf('length adjuster') !== -1 || option === 'yes' ? 'L' : '';
  }

  function springCode(value) {
    var option = normalize(value);
    var isPurple = option.indexOf('purple') !== -1;
    var isBlack = option.indexOf('black') !== -1 || option.indexOf('dark') !== -1;
    var isLinear = option.indexOf('linear') !== -1;

    if (isLinear && isBlack) return 'L';
    if (isLinear && isPurple) return 'M';
    if (isPurple) return 'H';
    if (isBlack) return 'P';
    return '';
  }

  function preloadAdjusterCode(value, sku) {
    var option = normalize(value);
    var normalizedSku = normalize(sku);

    if (!option || option === 'none' || option === 'no' || option.indexOf('hook wrench') !== -1) return 'X';
    if (option.indexOf('mpa') !== -1) return 'M';
    if (option.indexOf('showa') !== -1 || normalizedSku.indexOf('showa') !== -1) return 'S';
    if (option.indexOf('wp') !== -1 || normalizedSku.indexOf('-wp') !== -1) return 'W';
    if (option.indexOf('original') !== -1 || option.indexOf('oem') !== -1 || normalizedSku.indexOf('-oem') !== -1) {
      return 'H';
    }
    return option.indexOf('hydraulic') !== -1 || normalizedSku.indexOf('-hpa') !== -1 ? 'H' : '';
  }

  function isOemPreload(value, sku) {
    var option = normalize(value);
    var normalizedSku = normalize(sku);
    return option.indexOf('original') !== -1
      || option.indexOf('oem') !== -1
      || normalizedSku.indexOf('-oem') !== -1;
  }

  function passengerLoadCode(config) {
    var passengerPercentage = numberFrom(config.passengerPercentage);
    var luggagePercentage = numberFrom(config.luggagePercentage);
    var passengerCounts = Number.isFinite(passengerPercentage) && passengerPercentage >= 50;
    var luggageCounts = Number.isFinite(luggagePercentage) && luggagePercentage >= 50;
    if (!passengerCounts && !luggageCounts) return '';

    var passengerWeight = passengerCounts ? numberFrom(config.passengerWeightLbs) : 0;
    var luggageWeight = luggageCounts ? numberFrom(config.luggageWeightLbs) : 0;
    if (!Number.isFinite(passengerWeight) || !Number.isFinite(luggageWeight)) return null;

    return weightCodeFromPounds(passengerWeight + luggageWeight) || null;
  }

  function buildPartNumber(config) {
    var sku = String(config.sku == null ? '' : config.sku).trim();
    var baseSku = sku.slice(0, BASE_SKU_LENGTH);
    var rideHeight = config.rideHeightMm;
    var hasRideHeight = rideHeight != null && String(rideHeight).trim() !== '';

    var requiredCodes = [
      dampingCode(config.ridingStyle, config.stiffness),
      lengthAdjusterCode(config.lengthAdjuster),
      springCode(config.springColor),
      preloadAdjusterCode(config.preloadAdjuster, sku),
      weightCodeFromPounds(config.riderWeightLbs)
    ];
    var loadCode = passengerLoadCode(config);
    var heightCode = hasRideHeight ? heightCodeFromMillimeters(rideHeight) : '';

    if (baseSku.length !== BASE_SKU_LENGTH
      || requiredCodes.some(function (code) { return !code; })
      || loadCode === null
      || (hasRideHeight && !heightCode)) return '';
    return baseSku + requiredCodes.join('') + loadCode + heightCode
      + (isOemPreload(config.preloadAdjuster, sku) ? ' (OEM HPA TBD)' : '');
  }

  function elementValue(form, names) {
    for (var index = 0; index < names.length; index += 1) {
      var element = form.elements.namedItem(names[index]);
      if (element && String(element.value == null ? '' : element.value).trim() !== '') return element.value;
    }
    return '';
  }

  function productOptionValue(product, variant, optionNames) {
    if (!product || !variant || !Array.isArray(product.options) || !Array.isArray(variant.options)) return '';

    var wanted = optionNames.map(normalize);
    for (var index = 0; index < product.options.length; index += 1) {
      var productOption = product.options[index];
      var optionName = normalize(typeof productOption === 'string' ? productOption : productOption && productOption.name);
      if (wanted.some(function (name) { return optionName === name || optionName.indexOf(name) !== -1; })) {
        return variant.options[index] || '';
      }
    }
    return '';
  }

  function readProduct(form) {
    var jsonElement = form.querySelector('[data-epm-product-json]') || form.querySelector('[data-product-json]');
    if (!jsonElement) return null;
    try {
      return JSON.parse(jsonElement.textContent);
    } catch (error) {
      console.warn('Hyperpro PN: product JSON could not be read.', error);
      return null;
    }
  }

  function selectedVariant(form, product, suppliedVariant) {
    if (suppliedVariant) return suppliedVariant;
    if (form.querySelector) {
      var selectedData = form.querySelector('[data-epm-selected-variant]');
      if (selectedData) {
        try { return JSON.parse(selectedData.textContent); } catch (error) { return null; }
      }
    }
    if (!product || !Array.isArray(product.variants)) return null;

    var idElement = form.elements.namedItem('id');
    var selectedId = idElement ? String(idElement.value) : '';
    return product.variants.find(function (variant) { return String(variant.id) === selectedId; }) || null;
  }

  function updateForm(form, suppliedVariant) {
    if (!form) return;
    var output = form.querySelector('[data-hyperpro-pn]');
    if (!output) return;

    var product = readProduct(form);
    var variant = selectedVariant(form, product, suppliedVariant);
    if (!variant) {
      output.value = '';
      output.disabled = true;
      return;
    }

    var originalPreload = productOptionValue(product, variant, ['preload adjuster']);
    var explicitRideHeight = elementValue(form, ['properties[Ride Height]', 'properties[Ride height]']);
    var remarks = elementValue(form, ['properties[Additional remarks]']);
    var remarksLowering = loweringMillimetersFromRemarks(remarks);
    var partNumber = buildPartNumber({
      sku: variant.sku,
      ridingStyle: elementValue(form, ['properties[Riding Style]', 'custom_order_data_riding_style']),
      stiffness: elementValue(form, ['properties[Stiffness Setting]', 'custom_order_data_stiffness']),
      riderWeightLbs: elementValue(form, ['properties[Riding Weight]']),
      passengerWeightLbs: elementValue(form, ['properties[Passenger Weight]']),
      luggageWeightLbs: elementValue(form, ['properties[Luggage Weight]']),
      passengerPercentage: elementValue(form, ['properties[Passenger Seat Time (Percentage)]']),
      luggagePercentage: elementValue(form, ['properties[Luggage Seat Time (Percentage)]']),
      springColor: productOptionValue(product, variant, ['spring color', 'spring']),
      preloadAdjuster: originalPreload,
      lengthAdjuster: productOptionValue(product, variant, ['length adjuster']),
      rideHeightMm: explicitRideHeight || remarksLowering
    });

    output.value = partNumber;
    output.disabled = !partNumber;

  }

  function updateFromElement(element, suppliedVariant) {
    if (!element) return;
    var productForm = element.closest ? element.closest('product-form') : null;
    var form = productForm ? productForm.querySelector('[data-product-form]') : element.form;
    updateForm(form, suppliedVariant);
  }

  function syncFormData(event) {
    var form = event.currentTarget;
    updateForm(form);

    var output = form.querySelector('[data-hyperpro-pn]');
    if (!output || !output.value) {
      event.formData.delete('properties[_Hyperpro PN]');
      return;
    }

    event.formData.set(output.name, output.value);
  }

  function bindForm(form) {
    if (!form || !form.querySelector('[data-hyperpro-pn]')) return;

    updateForm(form);
    if (form.dataset.hyperproPnBound === 'true') return;

    form.addEventListener('formdata', syncFormData);
    form.dataset.hyperproPnBound = 'true';
  }

  var api = {
    buildPartNumber: buildPartNumber,
    dampingCode: dampingCode,
    heightCodeFromMillimeters: heightCodeFromMillimeters,
    isOemPreload: isOemPreload,
    lengthAdjusterCode: lengthAdjusterCode,
    loweringMillimetersFromRemarks: loweringMillimetersFromRemarks,
    passengerLoadCode: passengerLoadCode,
    preloadAdjusterCode: preloadAdjusterCode,
    springCode: springCode,
    updateForm: updateForm,
    weightCodeFromPounds: weightCodeFromPounds
  };

  global.HyperproPartNumberCoder = api;

  if (typeof document === 'undefined') return;

  document.addEventListener('theme:variant:change', function (event) {
    updateFromElement(event.target, event.detail && event.detail.variant);
  });

  ['input', 'change'].forEach(function (eventName) {
    document.addEventListener(eventName, function (event) {
      if (!event.target || !event.target.form || !event.target.form.querySelector('[data-hyperpro-pn]')) return;
      queueMicrotask(function () { updateForm(event.target.form); });
    });
  });

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest
      ? event.target.closest('[data-add-to-cart]')
      : null;
    if (!button || !button.form) return;

    bindForm(button.form);
    updateForm(button.form);
  }, true);

  document.addEventListener('submit', function (event) {
    if (event.target && event.target.matches('[data-product-form]')) updateForm(event.target);
  }, true);

  if (global.addEventListener) global.addEventListener('theme:section:update', function () {
    document.querySelectorAll('[data-product-form]').forEach(bindForm);
  });

  function initialize() {
    document.querySelectorAll('[data-product-form]').forEach(bindForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})(typeof window !== 'undefined' ? window : globalThis);
